import React, { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { api } from '../lib/api';
import { useAuthStore } from '../stores/authStore';
import {
  BarChart3, RefreshCw, Upload, ChevronRight, ChevronDown,
  AlertTriangle, Users, Loader, Building2, Play, Laptop, X,
  Copy, Download, Trash2, CheckCircle2, Clock, FileCode2,
} from 'lucide-react';
import clsx from 'clsx';

const fmtCas = (s: any) => {
  if (!s) return '-';
  try {
    const d = new Date(s);
    return d.toLocaleString('sl-SI', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return String(s); }
};

const fmtDatum = (s: any) => {
  if (!s) return '-';
  const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : String(s);
};

// ime datoteke z zetonom se izracuna enako kot pri prenosu, da se
// ukaz lahko izpise ze IZPOLNJEN - ograjno mesto je bilo past
const imeZetonDatoteke = (ime: string) =>
  'zeton_' + (ime || 'naprava').replace(/[^A-Za-z0-9_-]/g, '_') + '.txt';

const ukazNastavi = (imeNaprave?: string) =>
  'powershell -ExecutionPolicy Bypass -File .\\alfaberg_edavki.ps1 -Nastavi -ZetonDatoteka .\\'
  + (imeNaprave ? imeZetonDatoteke(imeNaprave) : 'IME_DATOTEKE_Z_ZETONOM.txt');

const UKAZ_CAKAJ =
  'powershell -ExecutionPolicy Bypass -File .\\alfaberg_edavki.ps1 -Cakaj';
const UKAZ_NAMESTI =
  'powershell -ExecutionPolicy Bypass -File .\\alfaberg_edavki.ps1 -Namesti';

const danesIso = () => new Date().toISOString().slice(0, 10);
const zacetekLeta = () => `${new Date().getFullYear()}-01-01`;

const obrazecCls = (o: string) => {
  if (o === 'DDV_O') return 'bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-300';
  if (o && o.startsWith('REK')) return 'bg-teal-100 dark:bg-teal-500/20 text-teal-700 dark:text-teal-300';
  if (o === 'VIES_KP') return 'bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-300';
  if (o === 'DOD_DDPO') return 'bg-violet-100 dark:bg-violet-500/20 text-violet-700 dark:text-violet-300';
  return 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300';
};

const periodaCls = (p: string) => {
  if (p === 'leto') return 'bg-violet-100 dark:bg-violet-500/20 text-violet-700 dark:text-violet-300';
  if (p === 'kvartal') return 'bg-sky-100 dark:bg-sky-500/20 text-sky-700 dark:text-sky-300';
  if (p === 'brez') return 'bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400';
  return 'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300';
};

const periodaNaziv = (p: string) => {
  if (p === 'leto') return 'letno';
  if (p === 'kvartal') return 'kvartalno';
  if (p === 'brez') return 'brez obdobja';
  return 'mesečno';
};

const izidCls = (s: string) => {
  if (s === 'ok') return 'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300';
  if (s === 'ni_dostopa') return 'bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-300';
  if (s === 'tip_neznan') return 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300';
  return 'bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-300';
};

const izidNaziv = (s: string) => {
  if (s === 'ok') return 'uspešno';
  if (s === 'ni_dostopa') return 'ni dostopa na eDavkih';
  if (s === 'tip_neznan') return 'tipa ni mogoče določiti';
  return 'napaka';
};

// Povzetek skupine kot ZNACKE, ne kot besedilo. Podatki so ze v
// o.dokumenti, zato backend ostane nedotaknjen.
const povzetekSkupine = (dokumenti: any[]) => {
  const zemljevid = new Map<string, { st: number; vrste: string[]; nazivi: string[] }>();
  for (const d of dokumenti || []) {
    const o = d.obrazec || '?';
    if (!zemljevid.has(o)) zemljevid.set(o, { st: 0, vrste: [], nazivi: [] });
    const e = zemljevid.get(o) as { st: number; vrste: string[]; nazivi: string[] };
    e.st += 1;
    if (d.vrsta_dohodka) {
      e.vrste.push(d.vrsta_dohodka);
      e.nazivi.push(d.vrsta_dohodka + (d.vrsta_dohodka_naziv ? ' — ' + d.vrsta_dohodka_naziv : ''));
    }
  }
  return Array.from(zemljevid.entries())
    .sort((x, y) => x[0].localeCompare(y[0]))
    .map(([obrazec, v]) => ({ obrazec, st: v.st, vrste: v.vrste, nazivi: v.nazivi }));
};

export function EdavkiPage() {
  const { selectedCompany } = useAuthStore();
  const baza = selectedCompany;

  const [podatki, setPodatki] = useState<any>(null);
  const [nalagam, setNalagam] = useState(false);
  const [posiljam, setPosiljam] = useState(false);
  const [odprto, setOdprto] = useState<string[]>([]);
  const [vkljuciNadomescene, setVkljuciNadomescene] = useState(false);
  const [zavezanec, setZavezanec] = useState<any>(null);
  const vhodRef = useRef<HTMLInputElement>(null);

  // zajem
  const [oknoZajem, setOknoZajem] = useState(false);
  const [podjetja, setPodjetja] = useState<any[]>([]);
  const [zavezanci, setZavezanci] = useState<Record<string, any>>({});
  const [izbrana, setIzbrana] = useState<string[]>([]);
  const [obdobjeOd, setObdobjeOd] = useState(zacetekLeta());
  const [obdobjeDo, setObdobjeDo] = useState(danesIso());
  const [stejDelavce, setStejDelavce] = useState(true);
  const [pripravljam, setPripravljam] = useState(false);
  const [nalog, setNalog] = useState<any>(null);

  // naprave
  const [oknoNaprave, setOknoNaprave] = useState(false);
  const [naprave, setNaprave] = useState<any[]>([]);
  const [imeNaprave, setImeNaprave] = useState('');
  const [noviZeton, setNoviZeton] = useState<any>(null);

  // -------------------------------------------------------------- statistika

  const naloziStatistiko = useCallback(() => {
    if (!baza) { setPodatki(null); return; }
    setNalagam(true);
    api.get('/edavki/' + baza.id + '/statistika',
      { params: { vkljuci_nadomescene: vkljuciNadomescene || undefined } })
      .then((r: any) => setPodatki(r.data))
      .catch(() => toast.error('Statistike ni bilo mogoče naložiti'))
      .finally(() => setNalagam(false));
  }, [baza, vkljuciNadomescene]);

  useEffect(() => { naloziStatistiko(); }, [naloziStatistiko]);

  useEffect(() => {
    if (!baza) { setZavezanec(null); return; }
    api.get('/edavki/' + baza.id + '/zavezanec')
      .then((r: any) => setZavezanec(r.data))
      .catch(() => setZavezanec(null));
  }, [baza]);

  // -------------------------------------------------------------- zajem

  const odpriZajem = () => {
    setOknoZajem(true);
    setPripravljam(true);
    api.get('/companies/')
      .then(async (r: any) => {
        const sez = r.data || [];
        setPodjetja(sez);
        setIzbrana(baza ? [baza.id] : []);
        const zem: Record<string, any> = {};
        await Promise.all(sez.map(async (c: any) => {
          try {
            const z = await api.get('/edavki/' + c.id + '/zavezanec');
            zem[c.id] = z.data;
          } catch {
            zem[c.id] = { uporabno: false, vir: 'napaka pri branju' };
          }
        }));
        setZavezanci(zem);
      })
      .catch(() => toast.error('Seznama podjetij ni bilo mogoče naložiti'))
      .finally(() => setPripravljam(false));
  };

  const preklopiPodjetje = (id: string) =>
    setIzbrana((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);

  const uporabna = () => podjetja.filter((c: any) => zavezanci[c.id]?.uporabno);

  const izberiVsa = () => setIzbrana(uporabna().map((c: any) => c.id));
  const odznaciVsa = () => setIzbrana([]);

  const zazeniZajem = () => {
    if (izbrana.length === 0) { toast.error('Izberite vsaj eno podjetje'); return; }
    setPripravljam(true);
    api.post('/edavki/nalogi', {
      company_ids: izbrana,
      obdobje_od: obdobjeOd,
      obdobje_do: obdobjeDo,
      stej_delavce: stejDelavce,
    })
      .then((r: any) => {
        setOknoZajem(false);
        setNalog({ id: r.data.id, status: r.data.status,
                   st_podjetij: r.data.st_podjetij, st_koncanih: 0, izidi: [] });
        toast.success(`Nalog ustvarjen za ${r.data.st_podjetij} podjetij. Zaženite skripto na računalniku.`);
        if (r.data.preklicanih_prejsnjih) {
          toast(`Preklican je bil ${r.data.preklicanih_prejsnjih} prejšnji nalog.`);
        }
      })
      .catch((e: any) => toast.error(String(e?.response?.data?.detail || 'Naloga ni bilo mogoče ustvariti')))
      .finally(() => setPripravljam(false));
  };

  // anketiranje poteka, enak interval kot AgentiPage
  useEffect(() => {
    if (!nalog?.id) return;
    if (nalog.status === 'koncano' || nalog.status === 'preklican') return;
    const i = setInterval(() => {
      api.get('/edavki/nalogi/' + nalog.id)
        .then((r: any) => {
          setNalog(r.data);
          if (r.data.status === 'koncano') {
            toast.success('Zajem končan.');
            naloziStatistiko();
          }
        })
        .catch(() => {});
    }, 10000);
    return () => clearInterval(i);
  }, [nalog, naloziStatistiko]);

  // -------------------------------------------------------------- naprave

  const odpriNaprave = () => {
    setOknoNaprave(true);
    setNoviZeton(null);
    api.get('/edavki/naprave')
      .then((r: any) => setNaprave(r.data || []))
      .catch(() => toast.error('Seznama naprav ni bilo mogoče naložiti'));
  };

  const dodajNapravo = () => {
    const ime = imeNaprave.trim();
    if (!ime) { toast.error('Vpišite ime naprave'); return; }
    api.post('/edavki/naprave', { ime })
      .then((r: any) => {
        setNoviZeton(r.data);
        setImeNaprave('');
        return api.get('/edavki/naprave');
      })
      .then((r: any) => setNaprave(r.data || []))
      .catch((e: any) => toast.error(String(e?.response?.data?.detail || 'Naprave ni bilo mogoče ustvariti')));
  };

  const prekliciNapravo = (id: string) => {
    api.delete('/edavki/naprave/' + id)
      .then(() => api.get('/edavki/naprave'))
      .then((r: any) => { setNaprave(r.data || []); toast.success('Naprava preklicana.'); })
      .catch(() => toast.error('Preklic ni uspel'));
  };

  const kopiraj = (t: string) => {
    navigator.clipboard.writeText(t)
      .then(() => toast.success('Kopirano.'))
      .catch(() => toast.error('Kopiranje ni uspelo'));
  };

  const prenesiSkripto = () => {
    // skripta pride s streznika ze nastavljena na ta naslov,
    // zato uporabniku ni treba nastavljati -Streznik
    api.get('/edavki/skripta', { responseType: 'blob' })
      .then((r: any) => {
        const u = URL.createObjectURL(new Blob([r.data], { type: 'text/plain' }));
        const a = document.createElement('a');
        a.href = u;
        a.download = 'alfaberg_edavki.ps1';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(u);
        toast.success('Skripta prenesena.');
      })
      .catch(() => toast.error('Skripte ni bilo mogoce prenesti'));
  };

  const prenesiZeton = (ime: string, zeton: string) => {
    const b = new Blob([zeton], { type: 'text/plain' });
    const u = URL.createObjectURL(b);
    const a = document.createElement('a');
    a.href = u;
    a.download = 'zeton_' + ime.replace(/[^A-Za-z0-9_-]/g, '_') + '.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(u);
  };

  // -------------------------------------------------------------- uvoz CSV

  const izberiDatoteko = () => vhodRef.current?.click();

  const naloziCsv = (e: React.ChangeEvent<HTMLInputElement>) => {
    const dat = e.target.files && e.target.files[0];
    if (!dat || !baza) return;
    const fd = new FormData();
    fd.append('datoteka', dat);
    setPosiljam(true);
    api.post('/edavki/' + baza.id + '/uvoz', fd)
      .then((r: any) => {
        const d = r.data || {};
        toast.success(
          `Uvoženih ${d.uvozenih ?? 0}, posodobljenih ${d.posodobljenih ?? 0}` +
          (d.preskocenih_tuja_davcna ? `, preskočenih ${d.preskocenih_tuja_davcna}` : '')
        );
        naloziStatistiko();
      })
      .catch((err: any) => toast.error(String(err?.response?.data?.detail || 'Uvoz ni uspel')))
      .finally(() => {
        setPosiljam(false);
        if (vhodRef.current) vhodRef.current.value = '';
      });
  };

  // -------------------------------------------------------------- prikaz

  if (!baza) {
    return (
      <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-12 text-center">
        <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <p className="text-gray-500 dark:text-gray-400">Najprej izberite podjetje</p>
      </div>
    );
  }

  const obdobja: any[] = (podatki && podatki.obdobja) || [];
  const zajem = podatki && podatki.zadnji_zajem;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-emerald-500" />
            Statistika eDavki
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            {baza.name}
            {zavezanec && zavezanec.davcna ? ` · DŠ ${zavezanec.davcna}` : ''}
            {zavezanec && zavezanec.tip ? ` · ${zavezanec.tip}` : ''}
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={odpriZajem}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 transition">
            <Play className="w-4 h-4" />
            Osveži podatke
          </button>
          <button onClick={odpriNaprave}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-gray-300 dark:border-gray-700 text-sm text-gray-700 dark:text-gray-200 hover:border-emerald-400 transition">
            <Laptop className="w-4 h-4" />
            Naprave
          </button>
          <input ref={vhodRef} type="file" accept=".csv" onChange={naloziCsv} className="hidden" />
          <button onClick={izberiDatoteko} disabled={posiljam}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-gray-300 dark:border-gray-700 text-sm text-gray-700 dark:text-gray-200 hover:border-emerald-400 disabled:opacity-50 transition">
            {posiljam ? <Loader className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            Naloži CSV
          </button>
          <button onClick={naloziStatistiko} disabled={nalagam}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-gray-300 dark:border-gray-700 text-sm text-gray-700 dark:text-gray-200 hover:border-emerald-400 disabled:opacity-50 transition">
            <RefreshCw className={clsx('w-4 h-4', nalagam && 'animate-spin')} />
            Osveži prikaz
          </button>
        </div>
      </div>

      {zavezanec && !zavezanec.uporabno ? (
        <div className="rounded-2xl border border-amber-300 dark:border-amber-500/40 bg-amber-50 dark:bg-amber-500/10 p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800 dark:text-amber-200">
            <p className="font-medium">Zavezanca ni mogoče določiti</p>
            <p className="mt-0.5">{zavezanec.vir}. Zajem za to podjetje ni mogoč.</p>
          </div>
        </div>
      ) : null}

      {nalog ? (
        <div className="rounded-2xl border border-emerald-300 dark:border-emerald-500/40 bg-emerald-50 dark:bg-emerald-500/10 p-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-sm text-emerald-900 dark:text-emerald-200">
              {nalog.status === 'koncano'
                ? <CheckCircle2 className="w-5 h-5" />
                : <Clock className="w-5 h-5 animate-pulse" />}
              <span className="font-medium">
                {nalog.status === 'caka' ? 'Nalog čaka na skripto' :
                 nalog.status === 'v_teku' ? 'Zajem poteka' :
                 nalog.status === 'koncano' ? 'Zajem končan' : nalog.status}
              </span>
              <span>{nalog.st_koncanih ?? 0} / {nalog.st_podjetij ?? 0}</span>
            </div>
            <button onClick={() => setNalog(null)}
              className="text-xs text-emerald-700 dark:text-emerald-300 hover:underline">
              skrij
            </button>
          </div>
          {nalog.status === 'caka' ? (
            <p className="text-xs text-emerald-800 dark:text-emerald-300 mt-2">
              Zaženite skripto <span className="font-mono">alfaberg_edavki.ps1</span> na
              svojem računalniku. Če že teče v načinu čakanja, bo nalog prevzela sama.
              Skripto in navodila dobite pod <span className="font-medium">Naprave</span>.
            </p>
          ) : null}
          {nalog.izidi && nalog.izidi.length > 0 ? (
            <div className="mt-3 space-y-1">
              {nalog.izidi.map((i: any) => {
                const p = (nalog.podjetja || []).find((x: any) => x.company_id === i.company_id);
                return (
                  <div key={i.company_id} className="flex items-center gap-2 text-xs">
                    <span className={clsx('px-2 py-0.5 rounded-lg', izidCls(i.status))}>
                      {izidNaziv(i.status)}
                    </span>
                    <span className="text-gray-700 dark:text-gray-300">{p ? p.naziv : i.company_id}</span>
                    {i.st_dokumentov ? <span className="text-gray-500">{i.st_dokumentov} dok.</span> : null}
                    {i.sporocilo ? <span className="text-gray-500">{i.sporocilo}</span> : null}
                  </div>
                );
              })}
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-4 flex items-center justify-between flex-wrap gap-3">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          {zajem
            ? <>Zadnji zajem: <span className="font-medium text-gray-900 dark:text-white">{fmtCas(zajem.zajeto_ob)}</span>
                {' · '}{zajem.st_dokumentov} dokumentov</>
            : <>Podatkov še ni. Zaženite zajem z gumbom »Osveži podatke«.</>}
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 cursor-pointer">
          <input type="checkbox" checked={vkljuciNadomescene}
                 onChange={(e) => setVkljuciNadomescene(e.target.checked)}
                 className="rounded border-gray-300" />
          Pokaži tudi nadomeščene
        </label>
      </div>

      {nalagam && !podatki ? (
        <div className="flex items-center justify-center h-40">
          <Loader className="w-8 h-8 text-emerald-500 animate-spin" />
        </div>
      ) : obdobja.length === 0 ? (
        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-12 text-center">
          <BarChart3 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 dark:text-gray-400">Za to podjetje še ni podatkov</p>
        </div>
      ) : (
        <div className="space-y-3">
          {obdobja.map((o: any) => {
            const jeOdprto = odprto.includes(o.kljuc);
            return (
              <div key={o.kljuc}
                className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 overflow-hidden">
                <button onClick={() => setOdprto((p) => p.includes(o.kljuc) ? p.filter((x) => x !== o.kljuc) : [...p, o.kljuc])}
                  className="w-full text-left p-4 flex items-center justify-between gap-3 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition">
                  <div className="flex items-center gap-3 min-w-0">
                    {jeOdprto
                      ? <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      : <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />}
                    <span className="font-semibold text-gray-900 dark:text-white">{o.naziv}</span>
                    <span className={clsx('px-2 py-0.5 rounded-lg text-xs', periodaCls(o.perioda))}>
                      {periodaNaziv(o.perioda)}
                    </span>
                  </div>
                  <span className="flex items-center gap-1.5 flex-wrap justify-end">
                    {povzetekSkupine(o.dokumenti).map((p: any) => (
                      <span key={p.obrazec} className="inline-flex items-center gap-1">
                        <span className={clsx('px-2 py-0.5 rounded-lg text-xs font-medium', obrazecCls(p.obrazec))}>
                          {p.st}x {p.obrazec}
                        </span>
                        {p.vrste.length > 0 ? (
                          <span title={p.nazivi.join('\n')}
                            className="px-2 py-0.5 rounded-lg text-xs bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 cursor-help">
                            {p.vrste.join(', ')}
                          </span>
                        ) : null}
                      </span>
                    ))}
                  </span>
                </button>

                {jeOdprto ? (
                  <div className="border-t border-gray-200 dark:border-gray-800 divide-y divide-gray-100 dark:divide-gray-800">
                    {o.dokumenti.map((d: any) => (
                      <div key={d.id} className="p-4 flex items-start justify-between gap-4 flex-wrap">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={clsx('px-2 py-0.5 rounded-lg text-xs font-medium', obrazecCls(d.obrazec))}>
                              {d.obrazec}
                            </span>
                            {d.vrsta_dohodka ? (
                              <span title={d.vrsta_dohodka_naziv || undefined}
                                className={clsx('px-2 py-0.5 rounded-lg text-xs bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300',
                                                d.vrsta_dohodka_naziv ? 'cursor-help' : '')}>
                                {d.vrsta_dohodka}
                                {d.vrsta_dohodka_kratek ? ' \u00b7 ' + d.vrsta_dohodka_kratek : ''}
                              </span>
                            ) : null}
                            {d.vrsta_dok === 'P' ? (
                              <span className="px-2 py-0.5 rounded-lg text-xs bg-orange-100 dark:bg-orange-500/20 text-orange-700 dark:text-orange-300">
                                popravek
                              </span>
                            ) : null}
                            {d.nadomescen ? (
                              <span className="px-2 py-0.5 rounded-lg text-xs bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-300">
                                nadomeščen
                              </span>
                            ) : null}
                            {d.po_roku ? (
                              <span className="px-2 py-0.5 rounded-lg text-xs bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-300">
                                po roku
                              </span>
                            ) : null}
                            {d.samoprijava ? (
                              <span className="px-2 py-0.5 rounded-lg text-xs bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-300">
                                samoprijava
                              </span>
                            ) : null}
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5 font-mono">{d.edp_stevilka}</p>
                          {d.izvorni_dok ? (
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                              izvorni dokument: <span className="font-mono">{d.izvorni_dok}</span>
                            </p>
                          ) : null}
                          {d.podrobnosti ? (
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{d.podrobnosti}</p>
                          ) : null}
                        </div>
                        <div className="text-right text-xs text-gray-500 dark:text-gray-400 space-y-0.5">
                          <p>oddano {fmtCas(d.datum_oddaje)}</p>
                          {d.mesec_izplacila ? <p>za mesec {d.mesec_izplacila}</p> : null}
                          {d.datum_izplacila ? <p>izplačilo {fmtDatum(d.datum_izplacila)}</p> : null}
                          {typeof d.st_delavcev === 'number' ? (
                            <p className="inline-flex items-center gap-1 text-gray-700 dark:text-gray-200 font-medium">
                              <Users className="w-3.5 h-3.5" />
                              {d.st_delavcev} delavcev
                            </p>
                          ) : null}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}

      {oknoZajem ? (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 w-full max-w-2xl mt-10">
            <div className="p-5 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Zajem z eDavkov</h2>
              <button onClick={() => setOknoZajem(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Zajem teče za izbrana podjetja, ne glede na to, katero je trenutno odprto.
              </p>

              <div className="grid grid-cols-2 gap-3">
                <label className="text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Od</span>
                  <input type="date" value={obdobjeOd} onChange={(e) => setObdobjeOd(e.target.value)}
                    className="mt-1 w-full rounded-xl border border-gray-300 dark:border-gray-700 bg-transparent px-3 py-2" />
                </label>
                <label className="text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Do</span>
                  <input type="date" value={obdobjeDo} onChange={(e) => setObdobjeDo(e.target.value)}
                    className="mt-1 w-full rounded-xl border border-gray-300 dark:border-gray-700 bg-transparent px-3 py-2" />
                </label>
              </div>

              <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 cursor-pointer">
                <input type="checkbox" checked={stejDelavce}
                  onChange={(e) => setStejDelavce(e.target.checked)} className="rounded border-gray-300" />
                Preštej delavce v REK-O (počasneje)
              </label>

              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  Podjetja ({izbrana.length} izbranih)
                </span>
                <span className="text-xs">
                  <button onClick={izberiVsa} className="text-emerald-600 hover:underline">izberi vsa</button>
                  <span className="text-gray-400"> · </span>
                  <button onClick={odznaciVsa} className="text-emerald-600 hover:underline">odznači</button>
                </span>
              </div>

              {pripravljam ? (
                <div className="flex items-center justify-center h-24">
                  <Loader className="w-6 h-6 text-emerald-500 animate-spin" />
                </div>
              ) : (
                <div className="max-h-72 overflow-y-auto rounded-xl border border-gray-200 dark:border-gray-800 divide-y divide-gray-100 dark:divide-gray-800">
                  {podjetja.map((c: any) => {
                    const z = zavezanci[c.id] || {};
                    return (
                      <label key={c.id}
                        className={clsx('flex items-center gap-3 p-3 text-sm',
                          z.uporabno ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50' : 'opacity-60')}>
                        <input type="checkbox" disabled={!z.uporabno}
                          checked={izbrana.includes(c.id)}
                          onChange={() => preklopiPodjetje(c.id)}
                          className="rounded border-gray-300" />
                        <span className="flex-1 min-w-0">
                          <span className="text-gray-900 dark:text-white">{c.name}</span>
                          {z.uporabno ? (
                            <span className="text-gray-500 dark:text-gray-400"> · {z.davcna} · {z.tip}</span>
                          ) : (
                            <span className="text-amber-600 dark:text-amber-400"> · {z.vir || 'ni podatka'}</span>
                          )}
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="p-5 border-t border-gray-200 dark:border-gray-800 flex items-center justify-end gap-2">
              <button onClick={() => setOknoZajem(false)}
                className="px-4 py-2 rounded-xl border border-gray-300 dark:border-gray-700 text-sm">
                Prekliči
              </button>
              <button onClick={zazeniZajem} disabled={pripravljam || izbrana.length === 0}
                className="px-4 py-2 rounded-xl bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50">
                Ustvari nalog
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {oknoNaprave ? (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 w-full max-w-2xl mt-10">
            <div className="p-5 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Naprave za zajem</h2>
              <button onClick={() => setOknoNaprave(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Naprava je računalnik, na katerem teče skripta za zajem. Žeton zna samo
                prevzeti nalog in oddati rezultat — do dokumentov ali eDavkov ne more.
              </p>

              <div className="rounded-xl border border-gray-200 dark:border-gray-800 p-4 space-y-3">
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  Namestitev na računalnik — enkrat
                </p>
                <p className="text-xs text-amber-700 dark:text-amber-400">
                  Skripte ne zaganjajte z dvoklikom — Windows jo odpre v urejevalniku.
                  Ukaza spodaj vpišite v PowerShell.
                </p>

                <div className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 text-xs flex items-center justify-center flex-shrink-0 mt-0.5">1</span>
                  <div className="flex-1">
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      Prenesite skripto in jo shranite v mapo, kjer bo ostala
                      (npr. <span className="font-mono text-xs">Dokumenti\Alfaberg</span>).
                    </p>
                    <button onClick={prenesiSkripto}
                      className="mt-1.5 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs">
                      <FileCode2 className="w-3.5 h-3.5" /> Prenesi skripto
                    </button>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 text-xs flex items-center justify-center flex-shrink-0 mt-0.5">2</span>
                  <p className="flex-1 text-sm text-gray-700 dark:text-gray-300">
                    Spodaj dodajte napravo in prenesite žeton v isto mapo.
                  </p>
                </div>

                <div className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 text-xs flex items-center justify-center flex-shrink-0 mt-0.5">3</span>
                  <div className="flex-1">
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      V PowerShellu v tej mapi poženite nastavitev. Izbrali boste
                      digitalno potrdilo; to se zgodi samo prvič.
                    </p>
                    <div className="mt-1.5 flex items-center gap-2">
                      <code className="flex-1 px-2 py-1.5 rounded-lg bg-gray-100 dark:bg-gray-800 text-xs font-mono break-all">
                        {ukazNastavi()}
                      </code>
                      <button onClick={() => kopiraj(ukazNastavi())}
                        className="p-1.5 rounded-lg border border-gray-300 dark:border-gray-700 text-gray-500 hover:text-emerald-600 flex-shrink-0">
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Namesto <span className="font-mono">IME_DATOTEKE_Z_ZETONOM.txt</span> vpišite
                      pravo ime prenesene datoteke. Ko napravo dodate spodaj, se izpiše
                      že izpolnjen ukaz. Po nastavitvi datoteko z žetonom zbrišite.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs flex items-center justify-center flex-shrink-0 mt-0.5">4</span>
                  <div className="flex-1">
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      Odslej pustite skripto teči; naloge bo prevzemala sama.
                    </p>
                    <div className="mt-1.5 flex items-center gap-2">
                      <code className="flex-1 px-2 py-1.5 rounded-lg bg-gray-100 dark:bg-gray-800 text-xs font-mono break-all">
                        {UKAZ_CAKAJ}
                      </code>
                      <button onClick={() => kopiraj(UKAZ_CAKAJ)}
                        className="p-1.5 rounded-lg border border-gray-300 dark:border-gray-700 text-gray-500 hover:text-emerald-600 flex-shrink-0">
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-3 pt-1 border-t border-gray-200 dark:border-gray-800">
                  <span className="w-6 h-6 rounded-full bg-sky-100 dark:bg-sky-500/20 text-sky-700 dark:text-sky-300 text-xs flex items-center justify-center flex-shrink-0 mt-2">5</span>
                  <div className="flex-1 pt-1.5">
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      <span className="font-medium">Da skripte ni treba zaganjati ročno:</span> ta
                      ukaz ustvari opravilo, ki jo ob vsaki prijavi v Windows zažene skrito.
                      Uporabnik nato v aplikaciji samo klikne »Osveži podatke«.
                    </p>
                    <div className="mt-1.5 flex items-center gap-2">
                      <code className="flex-1 px-2 py-1.5 rounded-lg bg-gray-100 dark:bg-gray-800 text-xs font-mono break-all">
                        {UKAZ_NAMESTI}
                      </code>
                      <button onClick={() => kopiraj(UKAZ_NAMESTI)}
                        className="p-1.5 rounded-lg border border-gray-300 dark:border-gray-700 text-gray-500 hover:text-emerald-600 flex-shrink-0">
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Skrbniške pravice niso potrebne. Opravilo odstranite z
                      <span className="font-mono"> -Odstrani</span>.
                    </p>
                    <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">
                      Če je digitalno potrdilo na ključku (npr. Halcom), bo ta ob vsaki
                      prijavi na eDavke morda zahteval PIN. Pri teku v ozadju je to nadležno —
                      takrat opravilo odstranite in skripto zaganjajte ročno.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input value={imeNaprave} onChange={(e) => setImeNaprave(e.target.value)}
                  placeholder="Ime naprave, npr. Evin prenosnik"
                  className="flex-1 rounded-xl border border-gray-300 dark:border-gray-700 bg-transparent px-3 py-2 text-sm" />
                <button onClick={dodajNapravo}
                  className="px-4 py-2 rounded-xl bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700">
                  Dodaj
                </button>
              </div>

              {noviZeton ? (
                <div className="rounded-xl border border-emerald-300 dark:border-emerald-500/40 bg-emerald-50 dark:bg-emerald-500/10 p-4 space-y-2">
                  <p className="text-sm font-medium text-emerald-900 dark:text-emerald-200">
                    Naprava »{noviZeton.ime}« ustvarjena
                  </p>
                  <p className="text-xs text-emerald-800 dark:text-emerald-300">
                    Prenesite žeton kot datoteko in ga uporabite pri prvem zagonu skripte:
                    <span className="font-mono"> -Nastavi -ZetonDatoteka</span>. Po nastavitvi datoteko zbrišite.
                  </p>
                  <div className="flex items-center gap-2">
                    <button onClick={() => prenesiZeton(noviZeton.ime, noviZeton.zeton)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs">
                      <Download className="w-3.5 h-3.5" /> Prenesi datoteko
                    </button>
                    <button onClick={() => kopiraj(noviZeton.zeton)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-emerald-400 text-emerald-700 dark:text-emerald-300 text-xs">
                      <Copy className="w-3.5 h-3.5" /> Kopiraj
                    </button>
                  </div>
                  <p className="text-xs text-emerald-800 dark:text-emerald-300 pt-1">
                    Ukaz za to napravo, z že vstavljenim imenom datoteke:
                  </p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 px-2 py-1.5 rounded-lg bg-white dark:bg-gray-900 border border-emerald-200 dark:border-emerald-500/30 text-xs font-mono break-all">
                      {ukazNastavi(noviZeton.ime)}
                    </code>
                    <button onClick={() => kopiraj(ukazNastavi(noviZeton.ime))}
                      className="p-1.5 rounded-lg border border-emerald-400 text-emerald-700 dark:text-emerald-300 flex-shrink-0">
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ) : null}

              <div className="rounded-xl border border-gray-200 dark:border-gray-800 divide-y divide-gray-100 dark:divide-gray-800">
                {naprave.length === 0 ? (
                  <p className="p-4 text-sm text-gray-500 dark:text-gray-400">Ni registriranih naprav.</p>
                ) : naprave.map((n: any) => (
                  <div key={n.id} className="p-3 flex items-start justify-between gap-3 text-sm">
                    <div className="min-w-0">
                      <p className={clsx('font-medium',
                        n.preklicana ? 'text-gray-400 line-through' : 'text-gray-900 dark:text-white')}>
                        {n.ime}
                      </p>
                      {n.potrdilo_naziv ? (
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-md">{n.potrdilo_naziv}</p>
                      ) : null}
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        ustvarjena {fmtCas(n.ustvarjeno_ob)}
                        {n.zadnjic_uporabljena ? ` · zadnjič ${fmtCas(n.zadnjic_uporabljena)}` : ' · še neuporabljena'}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {n.preklicana ? (
                        <span className="px-2 py-1 rounded-lg text-xs bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-300">
                          preklicana
                        </span>
                      ) : (
                        <>
                          <button onClick={() => prenesiZeton(n.ime, n.zeton)} title="Prenesi žeton"
                            className="p-1.5 rounded-lg border border-gray-300 dark:border-gray-700 text-gray-500 hover:text-emerald-600">
                            <Download className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={() => prekliciNapravo(n.id)} title="Prekliči napravo"
                            className="p-1.5 rounded-lg border border-gray-300 dark:border-gray-700 text-gray-500 hover:text-red-600">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="p-5 border-t border-gray-200 dark:border-gray-800 flex justify-end">
              <button onClick={() => setOknoNaprave(false)}
                className="px-4 py-2 rounded-xl border border-gray-300 dark:border-gray-700 text-sm">
                Zapri
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
